import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, STREAM_API } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';

// PPE live view — the "watch it working" screen.
//
// Why this page exists: PPE ran silently for months on this site and nobody could tell
// whether it was broken, misconfigured, or simply seeing no violations. The alert log
// only ever showed absence. This polls the server's PPE overlay so the read is visible:
// a green box on gear that is present, a red "NO helmet" on a body part with nothing on
// it, and a readout of what the model actually saw this scan.
//
// The overlay is produced by the SAME code path the alert uses (same model, imgsz, conf
// and pairing rule), so what you watch here is what the alert decides. A scan costs
// ~1.5s of CPU, so the default poll is deliberately slow — this is a diagnostic and
// supervision view, not a video wall. Use the Camera Wall for smooth live video.

const PERIODS = [
  { secs: 3,  label: '3s' },
  { secs: 5,  label: '5s' },
  { secs: 10, label: '10s' },
  { secs: 30, label: '30s' },
];

function PPEView({ cam, token, period, paused }) {
  const [src, setSrc]   = useState(null);
  const [err, setErr]   = useState(false);
  const [last, setLast] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!token || cam == null || paused) return undefined;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      // cache-buster: <img> would otherwise re-show the cached frame forever
      setSrc(`${STREAM_API}/ui/cameras/${cam}/annotated?mode=ppe&token=${token}&t=${Date.now()}`);
      setLast(new Date());
    };
    tick();
    timer.current = setInterval(tick, period * 1000);
    return () => { alive = false; clearInterval(timer.current); };
  }, [cam, token, period, paused]);

  return (
    <div className="ind-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ position: 'relative', background: '#000', aspectRatio: '16 / 9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {src && !err
          ? <img src={src} alt="PPE overlay" onError={() => setErr(true)} onLoad={() => setErr(false)}
                 style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <div className="ph" style={{ color: 'var(--ind-tx3)', fontSize: 13 }}>
              {err ? 'Frame capture failed — camera offline?' : 'Loading first scan…'}
            </div>}
        {paused && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, letterSpacing: '.08em' }}>PAUSED</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 12px',
                    fontFamily: 'var(--ind-mono)', fontSize: 11, color: 'var(--ind-tx3)' }}>
        <span>last scan {last ? last.toLocaleTimeString() : '—'}</span>
        <span style={{ marginLeft: 'auto' }}>
          green = gear present · red = missing · readout burned bottom-left
        </span>
      </div>
    </div>
  );
}

export default function IndustrialPPEPage() {
  const navigate = useNavigate();
  const [cameras, setCameras] = useState([]);
  const [cam, setCam]         = useState(null);
  const [token, setToken]     = useState(null);
  const [period, setPeriod]   = useState(5);
  const [paused, setPaused]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [d, t] = await Promise.all([
          apiFetch('/ui/cameras').then(r => r.json()),
          getToken(),
        ]);
        if (!alive) return;
        const cams = d.cameras || [];
        setCameras(cams);
        setToken(t);
        // Land on a camera that actually has PPE enabled — otherwise the first view is
        // a green-free frame and looks broken for the wrong reason.
        const withPpe = cams.find(c => (c.detectors || []).includes('helmet'));
        setCam((withPpe || cams[0])?.idx ?? null);
      } catch {
        if (alive) setError('Failed to load cameras');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const ppeCams = useMemo(
    () => cameras.filter(c => (c.detectors || []).includes('helmet')).map(c => c.idx),
    [cameras]);
  const enabled = cam != null && ppeCams.includes(cam);

  return (
    <IndustrialLayout
      eyebrow="Industrial"
      title="PPE Live View"
      subtitle={loading ? 'LOADING…'
        : `${ppeCams.length} OF ${cameras.length} CAMERAS RUNNING PPE`}
      banner={!loading && ppeCams.length === 0 && (
        <div className="ind-card" style={{ margin: '0 0 12px', borderColor: 'var(--ind-high)' }}>
          <b>PPE is not enabled on any camera.</b> Turn on the PPE / helmet model for a
          camera in <a href="/industrial/modules" onClick={e => { e.preventDefault(); navigate('/industrial/modules'); }}
            style={{ color: 'var(--ind-accent)' }}>AI Modules</a> first — nothing will be
          flagged until then.
        </div>
      )}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <select value={cam ?? ''} onChange={e => setCam(Number(e.target.value))}
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ind-line)',
                   background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 13 }}>
          {cameras.map(c => (
            <option key={c.idx} value={c.idx}>
              {(c.detectors || []).includes('helmet') ? '⛑️' : '○'} {c.name || `Camera ${c.idx}`}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: 4 }}>
          {PERIODS.map(p => (
            <button key={p.secs} className="ind-pill"
              onClick={() => setPeriod(p.secs)}
              style={period === p.secs
                ? { background: 'var(--ind-accent)', color: '#fff', borderColor: 'var(--ind-accent)' }
                : undefined}>{p.label}</button>
          ))}
        </div>

        <button className="ind-pill" onClick={() => setPaused(p => !p)}>
          {paused ? '▶ Resume' : '⏸ Pause'}
        </button>

        <button className="ind-pill" onClick={() => navigate('/industrial/modules')}>
          ⚙️ PPE settings
        </button>

        {!enabled && cam != null && !loading && (
          <span style={{ fontSize: 12, color: 'var(--ind-high)' }}>
            PPE is off for this camera — the overlay will stay empty
          </span>
        )}
      </div>

      {error
        ? <div className="ind-card" style={{ color: 'var(--ind-crit)' }}>{error}</div>
        : <PPEView cam={cam} token={token} period={period} paused={paused} />}

      <div className="ind-card" style={{ marginTop: 12, fontSize: 12, color: 'var(--ind-tx2)', lineHeight: 1.6 }}>
        <b>How to read this.</b> Each scan runs the PPE model on the full frame and pairs
        each body part with the gear on it. A <span style={{ color: 'var(--ind-ok)' }}>green</span> box
        means the gear was found; a <span style={{ color: 'var(--ind-crit)' }}>red</span> box
        means that body part had none — that is what raises a PPE alert, once it persists
        across consecutive scans. The bottom-left readout shows how many people were seen
        and what is being enforced, so an empty frame tells you whether the model saw
        nobody or saw everybody complying.
        <div style={{ marginTop: 8, color: 'var(--ind-tx3)' }}>
          Scans are throttled ({period}s here) because each one costs about 1.5 seconds of
          CPU. For smooth live video use the {' '}
          <a href="/industrial/wall" onClick={e => { e.preventDefault(); navigate('/industrial/wall'); }}
             style={{ color: 'var(--ind-accent)' }}>Camera Wall</a>.
        </div>
      </div>
    </IndustrialLayout>
  );
}
